import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Lightbulb,
  Loader2,
  MessageCircle,
  Send,
  Shield,
  Sparkles,
  X,
} from "lucide-react";
import {
  listMyFeedback,
  sendChat,
  submitFeedback,
  type ChatMessage,
  type FeedbackKind,
  type FeedbackItem,
} from "../lib/feedback";
import { navigate } from "../lib/router";
import { Button } from "./ui";

const STORAGE_KEY = "dp-chat-history";

type MsgRole = ChatMessage["role"] | "admin";
type Msg = { id: string; ts: number; role: MsgRole; content: string; subject?: string | null };

const ADMIN_SEEN_KEY = "dp-chat-admin-seen";

function loadAdminSeen(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_SEEN_KEY) || "{}");
  } catch {
    return {};
  }
}
function markAdminSeen(map: Record<string, number>): void {
  try {
    localStorage.setItem(ADMIN_SEEN_KEY, JSON.stringify(map));
  } catch {
    /* noop */
  }
}

// Bekannte Seiten-Verweise — werden im Bot-Reply detected und als
// Quick-Jump-Buttons unter der Nachricht angezeigt.
type PageRef = { keywords: string[]; path: string; label: string };
const PAGE_REFS: PageRef[] = [
  { keywords: ["Empfehlungs-Link", "Empfehlungslink", "Shop-Modus", "Shopmodus", "Kampagnen-Link"], path: "/links", label: "Empfehlungs-Links öffnen" },
  { keywords: ["Landing-Page", "Page-Editor", "Profilfoto", "Cover-Bild", "Theme", "Top-Pick", "QR-Code", "Inhalts-Blöcke", "Kompletter Shop", "Veröffentlich"], path: "/page-editor", label: "Landing-Page bearbeiten" },
  { keywords: ["Mein Team", "Recruitment", "Tier 2", "Tier 3", "Downline"], path: "/team", label: "Mein Team öffnen" },
  { keywords: ["Provisionen", "Provisions-Liste"], path: "/commissions", label: "Provisionen anzeigen" },
  { keywords: ["Auszahlung", "Gutschrift"], path: "/payouts", label: "Auszahlungen anzeigen" },
  { keywords: ["Statistik", "Konversion", "Conversion", "Top-Produkt"], path: "/stats", label: "Statistik öffnen" },
  { keywords: ["Stammdaten", "Passwort", "Bankverbindung", "IBAN"], path: "/account", label: "Stammdaten öffnen" },
  { keywords: ["Upgrade", "Voll-Agent", "Voll Agent"], path: "/upgrade", label: "Upgrade beantragen" },
  { keywords: ["/hilfe", "Anleitung", "ausführliche Hilfe", "vollständige Anleitung"], path: "/hilfe", label: "Anleitung öffnen" },
];

function loadHistory(): Msg[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function saveHistory(msgs: Msg[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(msgs.slice(-50)));
  } catch {
    /* noop */
  }
}

function detectPageRefs(text: string): PageRef[] {
  const lower = text.toLowerCase();
  const hits: PageRef[] = [];
  const seen = new Set<string>();
  for (const ref of PAGE_REFS) {
    if (seen.has(ref.path)) continue;
    if (ref.keywords.some((k) => lower.includes(k.toLowerCase()))) {
      hits.push(ref);
      seen.add(ref.path);
    }
  }
  return hits.slice(0, 3);
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"chat" | "wish">("chat");

  // Lock body scroll wenn offen
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fab-bottom-right z-40 grid place-items-center size-12 rounded-full brand-gradient text-white shadow-lg hover:brightness-110 active:scale-95 transition print:hidden"
        title="DP-Helfer öffnen"
        aria-label="Chat öffnen"
      >
        <MessageCircle className="size-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm grid place-items-center print:hidden"
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            className="w-full h-full sm:w-[min(720px,calc(100vw-2rem))] sm:h-[min(85vh,800px)] sm:rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden sm:my-4"
            role="dialog"
            aria-label="DP-Helfer Chat"
          >
            <Header tab={tab} onTabChange={setTab} onClose={close} />
            {tab === "chat" ? (
              <ChatPanel onNavigate={close} />
            ) : (
              <WishPanel onSent={() => setTab("chat")} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Header({
  tab,
  onTabChange,
  onClose,
}: {
  tab: "chat" | "wish";
  onTabChange: (t: "chat" | "wish") => void;
  onClose: () => void;
}) {
  return (
    <div className="border-b border-line bg-cream-soft/40 flex-shrink-0">
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <div className="flex items-center gap-2">
          <div className="grid size-8 place-items-center rounded-lg brand-gradient text-white">
            <Sparkles className="size-4" />
          </div>
          <div>
            <div className="text-base font-semibold leading-tight">DP-Helfer</div>
            <div className="text-[11px] text-ink-muted">
              Fragen + Wünsche rund ums Portal
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="size-9 grid place-items-center rounded-full text-ink-muted hover:bg-white hover:text-ink"
          aria-label="Schließen"
        >
          <X className="size-5" />
        </button>
      </div>
      <div className="flex px-3 gap-1">
        <TabBtn active={tab === "chat"} onClick={() => onTabChange("chat")}>
          <MessageCircle className="size-3.5" /> Frage stellen
        </TabBtn>
        <TabBtn active={tab === "wish"} onClick={() => onTabChange("wish")}>
          <Lightbulb className="size-3.5" /> Idee teilen
        </TabBtn>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        active
          ? "flex items-center gap-1 px-3 py-2 text-sm font-medium border-b-2 border-accent-orange text-ink -mb-px"
          : "flex items-center gap-1 px-3 py-2 text-sm font-medium text-ink-muted hover:text-ink"
      }
    >
      {children}
    </button>
  );
}

/* =============================================================================
 * Chat-Panel
 * ============================================================================*/

function ChatPanel({ onNavigate }: { onNavigate: () => void }) {
  const [messages, setMessages] = useState<Msg[]>(() => loadHistory());
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showFallbackCta, setShowFallbackCta] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Admin-Replies aus dem Feedback-Endpoint pollen + in den Chat einfuegen
  const { data: feedback } = useQuery({
    queryKey: ["my-feedback"],
    queryFn: () => listMyFeedback(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!feedback?.items?.length) return;
    const seen = loadAdminSeen();
    const newReplies: Msg[] = [];
    const updatedSeen = { ...seen };
    for (const fb of feedback.items as FeedbackItem[]) {
      if (!fb.admin_reply || !fb.admin_reply_at) continue;
      const ts = new Date(fb.admin_reply_at).getTime();
      if ((seen[fb.id] || 0) >= ts) continue;
      newReplies.push({
        id: `admin-${fb.id}-${ts}`,
        ts,
        role: "admin",
        content: fb.admin_reply,
        subject: fb.subject || fb.message.slice(0, 60),
      });
      updatedSeen[fb.id] = ts;
    }
    if (newReplies.length === 0) return;
    setMessages((prev) => {
      const existing = new Set(prev.map((m) => m.id));
      const merged = [...prev, ...newReplies.filter((m) => !existing.has(m.id))];
      merged.sort((a, b) => a.ts - b.ts);
      return merged;
    });
    markAdminSeen(updatedSeen);
  }, [feedback]);

  useEffect(() => {
    saveHistory(messages);
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [messages]);

  const ask = useMutation({
    mutationFn: (msg: string) =>
      sendChat({
        message: msg,
        // Nur user/assistant ans Backend — admin-Bubbles sind UI-only
        history: messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map(({ role, content }) => ({ role: role as "user" | "assistant", content })),
      }),
    onSuccess: (res, msg) => {
      const now = Date.now();
      setMessages((prev) => [
        ...prev,
        { id: `u${now}`, ts: now, role: "user", content: msg },
        { id: `a${now + 1}`, ts: now + 1, role: "assistant", content: res.reply },
      ]);
      setInput("");
      setShowFallbackCta(res.powered_by === "fallback");
    },
    onError: (e: Error) => setError(e.message || "Fehler"),
  });

  const quickIdea = useMutation({
    mutationFn: (subject: string) =>
      submitFeedback({
        kind: "idea",
        subject,
        message: subject,
        chat_context: messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map(({ role, content }) => ({ role: role as "user" | "assistant", content })),
      }),
    onSuccess: () => {
      const now = Date.now();
      setMessages((prev) => [
        ...prev,
        {
          id: `a${now}`,
          ts: now,
          role: "assistant",
          content:
            "Danke! Deine Idee ist beim Team angekommen. Wir melden uns, sobald wir geantwortet haben.",
        },
      ]);
      setShowFallbackCta(false);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || ask.isPending) return;
    ask.mutate(text);
  };

  const reset = () => {
    if (window.confirm("Chat-Verlauf wirklich löschen?")) {
      setMessages([]);
      saveHistory([]);
      setShowFallbackCta(false);
    }
  };

  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        style={{ overscrollBehavior: "contain" }}
      >
        {messages.length === 0 && <ChatHints onPick={(t) => ask.mutate(t)} />}
        {messages.map((m) => (
          <Bubble
            key={m.id}
            msg={m}
            onNavigate={(path) => {
              navigate(path);
              onNavigate();
            }}
          />
        ))}
        {ask.isPending && (
          <Bubble
            msg={{ id: "loading", ts: 0, role: "assistant", content: "" }}
            loading
            onNavigate={() => {}}
          />
        )}
        {showFallbackCta && messages.length > 0 && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-900">
            <div className="font-medium mb-1">Soll ich das als Idee weiterleiten?</div>
            <div className="text-xs mb-2">
              Wir lesen jede Einsendung und antworten dir persönlich.
            </div>
            <button
              onClick={() => {
                const lastUser = [...messages].reverse().find((m) => m.role === "user");
                if (lastUser) quickIdea.mutate(lastUser.content);
              }}
              disabled={quickIdea.isPending}
              className="inline-flex items-center gap-1 rounded-lg brand-gradient text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
            >
              {quickIdea.isPending ? "Sende…" : "Ja, ans Team senden"}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="px-4 py-2 text-xs text-rose-700 border-t border-rose-200 bg-rose-50">
          {error}
        </div>
      )}

      <form
        onSubmit={submit}
        className="border-t border-line p-3 flex gap-2 items-end flex-shrink-0 bg-white"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit(e as unknown as React.FormEvent);
            }
          }}
          placeholder="Frage tippen… (Enter sendet, Shift+Enter neue Zeile)"
          rows={2}
          maxLength={1500}
          className="flex-1 resize-none rounded-xl border border-line px-3 py-2 text-sm max-h-32"
        />
        <button
          type="submit"
          disabled={ask.isPending || !input.trim()}
          className="size-11 grid place-items-center rounded-xl brand-gradient text-white disabled:opacity-50 flex-shrink-0"
        >
          {ask.isPending ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <Send className="size-5" />
          )}
        </button>
      </form>
      {messages.length > 0 && (
        <button
          onClick={reset}
          className="text-[11px] text-ink-muted hover:text-ink px-4 pb-2 text-right flex-shrink-0"
        >
          Verlauf löschen
        </button>
      )}
    </>
  );
}

function ChatHints({ onPick }: { onPick: (s: string) => void }) {
  const hints = [
    "Wie funktioniert der Shop-Modus?",
    "Wann bekomme ich mein Geld?",
    "Wie veröffentliche ich meine Landing-Page?",
    "Warum sind die Preise versteckt?",
    "Wie wechsle ich mein Theme?",
    "Was ist ein Top-Pick?",
  ];
  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-cream-soft/60 border border-line p-4 text-sm text-ink-soft">
        <div className="font-semibold mb-1 text-ink">Hi! Ich bin dein DP-Helfer.</div>
        Frag mich alles über das Portal — wie ein Knopf funktioniert, wo du was findest,
        oder warum etwas so läuft wie es läuft. Bei Wünschen + Verbesserungs-Ideen wechsle
        oben auf <strong>„Idee teilen"</strong>.
      </div>
      <div className="text-xs uppercase tracking-wide font-semibold text-ink-muted">
        Probier zum Beispiel:
      </div>
      <div className="flex flex-wrap gap-1.5">
        {hints.map((h) => (
          <button
            key={h}
            onClick={() => onPick(h)}
            className="text-xs rounded-full border border-line bg-white px-3 py-1.5 hover:bg-cream-soft hover:border-line-strong"
          >
            {h}
          </button>
        ))}
      </div>
    </div>
  );
}

function Bubble({
  msg,
  loading,
  onNavigate,
}: {
  msg: Msg;
  loading?: boolean;
  onNavigate: (path: string) => void;
}) {
  const mine = msg.role === "user";
  const isAdmin = msg.role === "admin";
  const refs = !mine && !loading && !isAdmin ? detectPageRefs(msg.content) : [];

  if (isAdmin) {
    return (
      <div className="flex flex-col items-start gap-2 max-w-full">
        <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-700 flex items-center gap-1 pl-1">
          <Shield className="size-3" /> Antwort vom DP-Team
        </div>
        <div className="max-w-[90%] rounded-2xl px-4 py-3 text-sm bg-blue-50 text-blue-950 rounded-bl-sm border border-blue-200">
          {msg.subject && (
            <div className="text-[11px] text-blue-700 italic mb-1.5">
              Zu: „{msg.subject}"
            </div>
          )}
          <div className="whitespace-pre-line leading-relaxed">{msg.content}</div>
          <div className="text-[10px] text-blue-700/70 mt-2">
            {new Date(msg.ts).toLocaleString("de-DE")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={mine ? "flex justify-end" : "flex flex-col items-start gap-2 max-w-full"}>
      <div
        className={
          mine
            ? "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm brand-gradient text-white rounded-br-sm"
            : "max-w-[90%] rounded-2xl px-4 py-3 text-sm bg-cream-soft text-ink-soft rounded-bl-sm border border-line"
        }
      >
        {loading ? (
          <span className="inline-flex gap-1 items-center py-1">
            <span className="size-1.5 rounded-full bg-current animate-bounce" />
            <span
              className="size-1.5 rounded-full bg-current animate-bounce"
              style={{ animationDelay: "0.15s" }}
            />
            <span
              className="size-1.5 rounded-full bg-current animate-bounce"
              style={{ animationDelay: "0.3s" }}
            />
          </span>
        ) : (
          <div className="whitespace-pre-line leading-relaxed">{msg.content}</div>
        )}
      </div>

      {refs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 max-w-[90%]">
          {refs.map((r) => (
            <button
              key={r.path}
              onClick={() => onNavigate(r.path)}
              className="inline-flex items-center gap-1 text-xs rounded-full border border-accent-orange/40 bg-white text-accent-orange px-3 py-1 hover:bg-accent-orange hover:text-white transition"
            >
              {r.label} <ArrowRight className="size-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* =============================================================================
 * Wish-Panel
 * ============================================================================*/

function WishPanel({ onSent }: { onSent: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<FeedbackKind>("idea");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      submitFeedback({
        kind,
        subject: subject || null,
        message,
      }),
    onSuccess: () => {
      setSent(true);
      setSubject("");
      setMessage("");
      qc.invalidateQueries({ queryKey: ["my-feedback"] });
      setTimeout(() => {
        setSent(false);
        onSent();
      }, 2500);
    },
    onError: (e: Error) => setError(e.message || "Fehler"),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!message.trim()) return;
    submit.mutate();
  };

  const KIND_LABELS: Record<FeedbackKind, { label: string; hint: string }> = {
    idea: { label: "💡 Idee / Wunsch", hint: "Was würde dir das Portal noch besser machen?" },
    question: { label: "❓ Frage ans Team", hint: "Hat dir der Chat nicht geholfen? Frag uns direkt." },
    bug: { label: "🐞 Fehler melden", hint: "Was funktioniert nicht wie es sollte?" },
    other: { label: "💬 Sonstiges", hint: "Etwas anderes auf dem Herzen?" },
  };

  const current = KIND_LABELS[kind];

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5">
      <div className="max-w-xl mx-auto space-y-4">
        <div className="rounded-2xl bg-cream-soft/60 border border-line p-4">
          <h3 className="text-base font-semibold mb-1">Sag uns was du dir wünschst</h3>
          <p className="text-sm text-ink-soft">
            Wir lesen jede Einsendung persönlich, antworten dir per Mail oder hier im Chat
            und priorisieren danach die Entwicklung des Portals.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-ink-muted block mb-2">
              Was möchtest du senden?
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {(Object.keys(KIND_LABELS) as FeedbackKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={
                    kind === k
                      ? "text-xs rounded-xl px-2 py-2 brand-gradient text-white font-medium"
                      : "text-xs rounded-xl px-2 py-2 border border-line bg-white text-ink-muted hover:text-ink hover:border-line-strong"
                  }
                >
                  {KIND_LABELS[k].label}
                </button>
              ))}
            </div>
            <p className="text-xs text-ink-muted mt-1.5 italic">{current.hint}</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-ink-muted block mb-1.5">
              Kurz-Titel (optional)
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="z.B. Möchte Insta-Story-Vorlage"
              maxLength={200}
              className="w-full rounded-xl border border-line px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-ink-muted block mb-1.5">
              Deine Nachricht *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Schreib einfach drauflos…"
              rows={8}
              maxLength={4000}
              className="w-full rounded-xl border border-line px-3 py-2 text-sm resize-y"
              required
            />
            <div className="text-[11px] text-ink-muted text-right mt-1">
              {message.length} / 4000
            </div>
          </div>

          {error && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-800">
              {error}
            </div>
          )}

          {sent && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-3 text-sm text-emerald-800">
              ✓ Danke! Deine Nachricht ist beim Team angekommen.
            </div>
          )}

          <Button
            type="submit"
            disabled={submit.isPending || !message.trim()}
            className="w-full"
          >
            {submit.isPending ? "Sende…" : "Ans Team senden →"}
          </Button>
        </form>
      </div>
    </div>
  );
}
